import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Cart, CartDeliveryAddress, CartItemData } from '../';
import { DeliveryType } from '../types';

const SET_CART = 'SET_CART';
const CLEAR_CART = 'CLEAR_CART';
const ADD_CART_ITEM = 'ADD_CART_ITEM';

const REMOVE_CART_ITEM = 'REMOVE_CART_ITEM';

const UPDATE_CART_ITEM = 'UPDATE_CART_ITEM';
const MOVE_CART_ITEM = 'MOVE_CART_ITEM';

const UPDATE_CART_DELIVERY = 'UPDATE_CART_DELIVERY';

// const SET_CART_STORE = 'SET_CART_STORE';
const CLEAR_CART_STORE = 'CLEAR_CART_STORE';

type NewDelivery = {
  type?: DeliveryType;
  isReOrder?: boolean;
  siloId?: number | null;
  comment?: string;
  deliveryAddress?: CartDeliveryAddress;
};

export type UpdateDelivery = {
  /** required! - the shopping cart id. This could be the customerId, the retailerId or any other value (only used locally) */
  cartKey: string;
  /** The delivery to update */
  deliveryId: string;

  deliveryAddress?: CartDeliveryAddress;
  deliveryFrom?: string;
  deliveryTo?: string;
  comment?: string;
  siloId?: number;
  type?: DeliveryType;

  /** all items should be from a single location */
  locationId?: number;

  /** if true, the delivery date will be required for ALL deliveries */
  requiredDeliveryDate?: boolean;


  /** delivery time for mills */
  deliveryTime?: string;
};

export type RemoveItem = {
  /** required! - the shopping cart id. This could be the customerId, the retailerId or any other value (only used locally) */
  cartKey: string;
  /** The item you're removing */
  itemId: number;
  /** The delivery the item is currently in */
  deliveryId: string;
};

export type UpdateItem = {
  /** required! - the shopping cart id. This could be the customerId, the retailerId or any other value (only used locally) */
  cartKey: string;
  /** The id of the item you're updating */
  itemId: number;
  /** The delivery the item is currently in */
  deliveryId: string;
  /** The amount of the dispensing quantity (amount x quantity = totalQuantity) */
  amount?: number;
  /** The price for '1' of the dispensing quantity */
  price?: number;
  /** Optional per-article note (Artikelnotiz) — maps to OrderLineItemInput.comment */
  comment?: string;
};

export type MoveItem = {
  /** required! - the shopping cart id. This could be the customerId, the retailerId or any other value (only used locally) */
  cartKey: string;
  /** The id of the item you're updating */
  itemId: number;
  /** The delivery the item is currently in */
  deliveryId: string;
  /** The delivery you're moving to */
  newDeliveryId?: string;
  /** optionally create a new delivery for the item, even if another already exists in the cart for the given location/type */
  newDelivery?: NewDelivery;
  /** if true, the delivery date will be required for ALL deliveries */
  requiredDeliveryDate?: boolean;
  /** if true, moving an item into an existing delivery should be blocked */
  singleItemPerDelivery?: boolean;
};

export type AddItem = {
  /** required! - the shopping cart id. This could be the customerId, the retailerId or any other value (only used locally) */
  cartKey: string;
  /** ItemData used by the cartLogic, must be provided when adding items. */
  itemData: CartItemData;
  /** The amount of the dispensing quantity (amount x quantity = totalQuantity) */
  amount: number;
  /** The price for '1' of the dispensing quantity, if omited use the article price */
  price?: number;
  /** If provided this overrides the defaultLocation of the item */
  locationId?: number;
  /** If provided the item will be added to this pre-existing delivery, make sure delivery.locationId == locationId if you're providing a locationId */
  deliveryId?: string;
  /** If provided the item will be added to a new delivery, even if another already exists in the cart for the given location */
  delivery?: NewDelivery;
  /** if true, the delivery date will be required for ALL deliveries */
  requiredDeliveryDate?: boolean;
  /** If provided the item will be added to a new delivery, even if another already exists in the cart for the given location */
  type?: DeliveryType;
  /** if true, always create a new delivery when adding an item */
  singleItemPerDelivery?: boolean;
};

export const addCartItem = createAction<AddItem>(ADD_CART_ITEM);
export const removeCartItem = createAction<RemoveItem>(REMOVE_CART_ITEM);
export const updateCartItem = createAction<UpdateItem>(UPDATE_CART_ITEM);
export const moveCartItem = createAction<MoveItem>(MOVE_CART_ITEM);
export const updateCartDelivery =
  createAction<UpdateDelivery>(UPDATE_CART_DELIVERY);
export const setCart = createAction<{ cartKey: string; cart: Cart }>(SET_CART);
export const clearCart = createAsyncThunk(
  CLEAR_CART,
  async (payload: { cartKey: string }) => {
    return payload;
  },
);
export const clearCartStore = createAction(CLEAR_CART_STORE);
